import {useTranslation} from '@/translations/i18n'
import {MedicalReportSmall} from '../../types'
import {useRef, useState} from 'react'
import {ComboboxProvider, SelectProvider} from '@ariakit/react'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Label} from '@/components/ui/label/Label'
import {
    SelectComboboxInput,
    SelectComboboxList,
    SelectComboboxPopover,
    SelectItem,
    SelectTrigger,
    SelectVirtualList
} from '@/components/ui/select-atoms/SelectAtoms'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {useGetInfiniteMedicalReportsByPatient} from '@/features/medical-reports/queries/useGetInfiniteMedicalReportsByPatient'
import {useInputSearch} from '@/hooks/useInputSearch'
import {MedicalReportGroup} from '@/types'
import dayjs from 'dayjs'
import {Button} from '@/components/ui/button/Button'
import {EyeIcon} from '@/components/ui/icon/Icon'
import {MedicalReportDetailsModal} from '@/features/medical-reports/components/medical-report-details-modal/MedicalReportDetailsModal'
import {useGetMedicalReport} from '@/features/medical-reports/queries/useGetMedicalReport'

export const InputSelectReportByProject = ({
    label,
    placeholder,
    patientId,
    projectId,
    group,
    value,
    onChange,
    errorMessage
}: {
    label: string
    patientId?: string
    projectId?: string
    group?: MedicalReportGroup
    value?: MedicalReportSmall | string | null
    onChange: (value: MedicalReportSmall | null) => void
    placeholder?: string
    errorMessage?: string
}) => {
    const {t} = useTranslation()
    const [isQueryEnabled, setIsQueryEnabled] = useState(false)
    const [isModalDetailsOpened, setIsModalDetailsOpened] = useState(false)
    const inputRef = useRef(null)
    const {searchValue, onSearch, onResetSearch} = useInputSearch()

    const medicalReportsQuery = useGetInfiniteMedicalReportsByPatient({
        urlParams: {
            patientId: patientId ?? ''
        },
        params: {
            group,
            projectId: projectId ?? undefined,
            limit: 20,
            search: searchValue
        },
        options: {
            enabled: isQueryEnabled && !!patientId && !!projectId
        }
    })

    // This approach is used to avoid the issue of the medical report not being fetched when the value is a string
    // It is not the best approach, but it is the only way to avoid the issue caused by question/answer structure
    const medicalReport = useGetMedicalReport({
        urlParams: {medicalReportId: typeof value === 'string' ? value : (value?.id ?? '')},
        params: {responseType: 'small'},
        options: {enabled: typeof value === 'string' && !!value}
    })

    const sanitizedValue = typeof value === 'string' ? medicalReport.data : (value ?? null)

    const handleClickMedicalReportDetails = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()
        setIsModalDetailsOpened(true)
    }

    if (value && !sanitizedValue?.id) {
        return null
    }

    return (
        <>
            <ComboboxProvider setValue={onSearch} resetValueOnHide>
                <SelectProvider
                    value={sanitizedValue?.id ?? ''}
                    setValue={value =>
                        onChange(medicalReportsQuery.remappedData.find(report => value == report.id) ?? null)
                    }
                    setOpen={isOpen => {
                        setIsQueryEnabled(isOpen)
                        onResetSearch(inputRef)
                    }}
                >
                    <Flexbox direction="column" gap={1.5} fullWidth>
                        <Label>{label}</Label>
                        <SelectTrigger
                            placeholder={placeholder}
                            fullWidth
                            value={sanitizedValue?.id}
                            displayValue={() =>
                                sanitizedValue?.medicalRecordReportType.name ? (
                                    <Flexbox fullWidth justify="space-between" align="center" gap={1}>
                                        {t('medical_records:medical_report_type_date', {
                                            medicalReportType: sanitizedValue.medicalRecordReportType.name,
                                            date: dayjs(sanitizedValue.date).format('DD/MM/YYYY')
                                        })}
                                        <Button
                                            shape="text"
                                            variant="ghost"
                                            style={{cursor: 'pointer'}}
                                            onClick={handleClickMedicalReportDetails}
                                        >
                                            <EyeIcon />
                                        </Button>
                                    </Flexbox>
                                ) : (
                                    ''
                                )
                            }
                            hasError={!!errorMessage}
                        />
                        <Flexbox align="center" gap={errorMessage ? 1.5 : 0}>
                            <InputStatusIcon hasError={!!errorMessage} />
                            <InputHelpText error={errorMessage} />
                        </Flexbox>
                        <SelectComboboxPopover sameWidth>
                            <SelectComboboxInput ref={inputRef} placeholder={t('commons:search_placeholder')} />
                            <SelectComboboxList>
                                {medicalReportsQuery.isLoading ? (
                                    <>
                                        {Array.from({length: 6}, (_, index) => (
                                            <Skeleton key={index} height={32} />
                                        ))}
                                    </>
                                ) : medicalReportsQuery.isError ? (
                                    <InputHelpText helpText={t('commons:no_results')} />
                                ) : medicalReportsQuery.remappedData.length > 0 ? (
                                    <SelectVirtualList
                                        data={medicalReportsQuery.remappedData}
                                        style={{height: 200}}
                                        totalCount={25}
                                        overscan={100}
                                        itemContent={(itemIndex, item) => (
                                            <SelectItem key={itemIndex} value={item.id} hasItemCheckbox>
                                                {t('medical_records:medical_report_type_date', {
                                                    medicalReportType: item.medicalRecordReportType.name,
                                                    date: dayjs(item.date).format('DD/MM/YYYY')
                                                })}
                                            </SelectItem>
                                        )}
                                        endReached={() =>
                                            medicalReportsQuery.hasNextPage && medicalReportsQuery.fetchNextPage()
                                        }
                                    />
                                ) : (
                                    <InputHelpText helpText={t('commons:no_results')} />
                                )}
                            </SelectComboboxList>
                        </SelectComboboxPopover>
                    </Flexbox>
                </SelectProvider>
            </ComboboxProvider>
            {isModalDetailsOpened && sanitizedValue && (
                <MedicalReportDetailsModal
                    medicalReport={sanitizedValue}
                    patientId={patientId ?? ''}
                    showEditButton={false}
                    onClose={() => setIsModalDetailsOpened(false)}
                />
            )}
        </>
    )
}
